import getCookie from "@/shared/utils/getCookie";

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

interface HttpRequestOptions<T> {
	method: HttpMethod;
	headers?: HeadersInit;
	body?: T;
}

async function accessTokenMiddleware() {
	const { refreshToken } = await import("@/services/Users");
	const { useUserStore } = await import("@/stores/useUserStore");

	const access_token = getCookie("access_token");
	if (access_token) {
		return;
	}

	const userStore = useUserStore();
	const refresh_token = getCookie("refresh_token");
	if (refresh_token) {
		await refreshToken({
			userId: userStore.user?.id as string,
			refreshToken: refresh_token,
		});
		return;
	}

	userStore.logout();
}

async function httpRequest<T>(url: string, options: HttpRequestOptions<T>): Promise<Response> {
	interface DefaultHeaders {
		"Content-Type": string;
		Accept: string;
		Authorization?: string;
	}

	const DEFAULT_HEADERS: DefaultHeaders = {
		"Content-Type": "application/json;",
		Accept: "application/json",
	};

	const SERVER_HOST = import.meta.env.VITE_SERVER_HOST;
	const SERVER_PORT = import.meta.env.VITE_SERVER_PORT;

	const headers = { ...DEFAULT_HEADERS, ...options.headers };

	if (url.includes("/auth")) {
		await accessTokenMiddleware();
		headers.Authorization = `Bearer ${getCookie("access_token")}`;
	}

	const path =
		import.meta.env.VITE_AMBIENT !== "production"
			? `${SERVER_HOST}${SERVER_PORT}${url}`
			: `${SERVER_HOST}${url}`;

	return await fetch(path, {
		method: options.method,
		headers: headers,
		body: options.body ? JSON.stringify(options.body) : null,
	});
}

export async function get(endpoint: string, headers?: HeadersInit) {
	return await httpRequest(endpoint, { method: "GET", headers });
}

export async function post<T>(endpoint: string, body: T, headers?: HeadersInit) {
	return await httpRequest(endpoint, { method: "POST", headers, body });
}

export async function put<T>(endpoint: string, body: T, headers?: HeadersInit) {
	return await httpRequest(endpoint, { method: "PUT", headers, body });
}

export async function patch<T>(endpoint: string, body: T, headers?: HeadersInit) {
	return await httpRequest(endpoint, { method: "PATCH", headers, body });
}

export async function del<T>(endpoint: string, body?: T, headers?: HeadersInit) {
	return await httpRequest(endpoint, { method: "DELETE", headers, body });
}
